import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminHeaders } from '../../../utils';
import { formatZipOptOut } from '../../../utils/profileFields';
import './ReportsDirectoryPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface Volunteer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
  birthMonth: string;
  birthYear: string;
  isMinor: boolean;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyRelationship: string;
  emergencyPhoneNumber: string;
  passcode: string;
  createdAt: string;
  lastCheckIn?: string;
  totalHours?: number;
  isActive: boolean;
}

const BIRTHDAY_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatListedHours(hours?: number): string {
  const rounded = Math.round((Number(hours) || 0) * 10) / 10;
  return `${rounded}h`;
}

function formatBirthday(birthMonth: string): string {
  const value = (birthMonth || '').trim();
  if (!value || value.toLowerCase() === 'out') {
    return 'opt out';
  }
  const monthNumber = Number(value);
  if (Number.isInteger(monthNumber) && monthNumber >= 1 && monthNumber <= 12) {
    return BIRTHDAY_MONTHS[monthNumber - 1];
  }
  return value;
}

const ReportsDirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'pdf'>('pdf');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeEmergency, setIncludeEmergency] = useState(true);
  const [includeHours, setIncludeHours] = useState(false);
  const [includePasscode, setIncludePasscode] = useState(false);
  const [includeBirthday, setIncludeBirthday] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'passcode'>('name');

  useEffect(() => {
    fetchVolunteers();
  }, []);

  useEffect(() => {
    filterVolunteers();
  }, [volunteers, includeInactive]);

  const fetchVolunteers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/volunteers`, {
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setVolunteers(data);
      } else if (response.status === 401) {
        navigate('/admin-login');
      } else {
        setMessage('Failed to load volunteers');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const filterVolunteers = () => {
    let filtered = volunteers;

    // Filter by active status
    if (!includeInactive) {
      filtered = filtered.filter(volunteer => volunteer.isActive);
    }

    setFilteredVolunteers(filtered);
  };

  const sortVolunteers = (volunteers: Volunteer[]) => {
    return [...volunteers].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'passcode':
          return a.passcode.localeCompare(b.passcode);
        default:
          return 0;
      }
    });
  };

  const generateCSV = () => {
    const sortedVolunteers = sortVolunteers(filteredVolunteers);
    
    let csvContent = 'Name,Email,Phone,Zip Code';
    
    if (includePasscode) {
      csvContent += ',User code';
    }
    
    csvContent += ',Status';
    
    if (includeHours) {
      csvContent += ',Total Hours,Last Check-in';
    }
    
    if (includeBirthday) {
      csvContent += ',Birthday';
    }

    if (includeEmergency) {
      csvContent += ',Emergency Contact,Relationship,Emergency Phone';
    }
    
    csvContent += '\n';

    sortedVolunteers.forEach(volunteer => {
      const row = [
        `"${volunteer.firstName} ${volunteer.lastName}"`,
        `"${volunteer.email}"`,
        `"${volunteer.phoneNumber}"`,
        `"${formatZipOptOut(volunteer.zipCode)}"`
      ];

      if (includePasscode) {
        row.push(`"${volunteer.passcode}"`);
      }

      row.push(`"${volunteer.isActive ? 'Active' : 'Inactive'}"`);

      if (includeHours) {
        row.push(
          `"${volunteer.totalHours || 0}"`,
          `"${volunteer.lastCheckIn || 'Never'}"`
        );
      }

      if (includeBirthday) {
        row.push(`"${formatBirthday(volunteer.birthMonth)}"`);
      }

      if (includeEmergency) {
        row.push(
          `"${volunteer.emergencyFirstName} ${volunteer.emergencyLastName}"`,
          `"${volunteer.emergencyRelationship}"`,
          `"${volunteer.emergencyPhoneNumber}"`
        );
      }

      csvContent += row.join(',') + '\n';
    });

    return csvContent;
  };

  const generatePDF = () => {
    // For now, we'll generate a simple HTML table that can be printed as PDF
    const sortedVolunteers = sortVolunteers(filteredVolunteers);
    
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>FabMo Volunteer Directory</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #16a34a; text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f0fdf4; }
          .minor { color: #dc2626; font-weight: bold; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>FabMo Volunteer Directory</h1>
        <p>Generated on: ${new Date().toLocaleDateString()}</p>
        <p>Total Volunteers: ${sortedVolunteers.length}</p>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Zip Code</th>
              ${includePasscode ? '<th>User code</th>' : ''}
              <th>Status</th>
              ${includeHours ? '<th>Total Hours</th>' : ''}
              ${includeBirthday ? '<th>Birthday</th>' : ''}
              ${includeEmergency ? '<th>Emergency Contact</th><th>Relationship</th><th>Emergency Phone</th>' : ''}
            </tr>
          </thead>
          <tbody>
    `;

    sortedVolunteers.forEach(volunteer => {
      htmlContent += `
        <tr>
          <td>${volunteer.firstName} ${volunteer.lastName}${volunteer.isMinor ? ' <span class="minor">(Minor)</span>' : ''}</td>
          <td>${volunteer.email}</td>
          <td>${volunteer.phoneNumber}</td>
          <td>${formatZipOptOut(volunteer.zipCode)}</td>
          ${includePasscode ? `<td>${volunteer.passcode}</td>` : ''}
          <td>${volunteer.isActive ? 'Active' : 'Inactive'}</td>
          ${includeHours ? `<td>${volunteer.totalHours || 0}</td>` : ''}
          ${includeBirthday ? `<td>${formatBirthday(volunteer.birthMonth)}</td>` : ''}
          ${includeEmergency ? `<td>${volunteer.emergencyFirstName} ${volunteer.emergencyLastName}</td><td>${volunteer.emergencyRelationship}</td><td>${volunteer.emergencyPhoneNumber}</td>` : ''}
        </tr>
      `;
    });

    htmlContent += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    return htmlContent;
  };

  const handleExport = () => {
    if (filteredVolunteers.length === 0) {
      setMessage('No volunteers to export');
      return;
    }

    setMessage('');

    if (selectedFormat === 'csv') {
      const csvContent = generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fabmo-volunteer-directory-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage('CSV file downloaded successfully!');
    } else {
      const htmlContent = generatePDF();
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        setMessage('PDF ready for printing!');
      }
    }
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  return (
    <div className="reports-directory-container">
      <div className="reports-directory-card">
        <div className="reports-directory-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="reports-directory-title">Volunteer Directory & Export</h1>
          <p className="reports-directory-subtitle">
            Manage volunteer information and generate comprehensive directory exports
          </p>
        </div>

        <div className="reports-directory-content">
          {/* Export Options */}
          <div className="export-options">
            <h3>Export Options</h3>
            
            <div className="option-group">
              <label className="option-label">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={selectedFormat === 'pdf'}
                  onChange={(e) => setSelectedFormat(e.target.value as 'csv' | 'pdf')}
                />
                <span>PDF Format</span>
              </label>
              <label className="option-label">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={selectedFormat === 'csv'}
                  onChange={(e) => setSelectedFormat(e.target.value as 'csv' | 'pdf')}
                />
                <span>CSV Format</span>
              </label>
            </div>

            <div className="option-group">
              <label className="option-label">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                <span>Include Inactive Volunteers</span>
              </label>
              <label className="option-label">
                <input
                  type="checkbox"
                  checked={includeEmergency}
                  onChange={(e) => setIncludeEmergency(e.target.checked)}
                />
                <span>Include Emergency Contacts</span>
              </label>
              <label className="option-label">
                <input
                  type="checkbox"
                  checked={includeBirthday}
                  onChange={(e) => setIncludeBirthday(e.target.checked)}
                />
                <span>Include Birthday</span>
              </label>
              <label className="option-label">
                <input
                  type="checkbox"
                  checked={includeHours}
                  onChange={(e) => setIncludeHours(e.target.checked)}
                />
                <span>Include Volunteer Hours</span>
              </label>
              <label className="option-label">
                <input
                  type="checkbox"
                  checked={includePasscode}
                  onChange={(e) => setIncludePasscode(e.target.checked)}
                />
                <span>Include user code</span>
              </label>
            </div>

            <div className="option-group">
              <label className="option-label">
                Sort by:
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'email' | 'passcode')}
                  className="sort-select"
                >
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="passcode">User code</option>
                </select>
              </label>
            </div>

            <button
              className="export-button"
              onClick={handleExport}
              disabled={isLoading || filteredVolunteers.length === 0}
            >
              {isLoading ? 'Loading...' : `Export ${selectedFormat.toUpperCase()}`}
            </button>
          </div>

          {/* Preview */}
          <div className="directory-preview">
            <h3>Directory Preview ({filteredVolunteers.length} volunteers)</h3>
            {isLoading ? (
              <div className="loading">Loading volunteers...</div>
            ) : filteredVolunteers.length === 0 ? (
              <div className="no-volunteers">No volunteers found</div>
            ) : (
              <div className="directory-table">
                <div className="directory-table-inner">
                  <div className="table-header">
                    <div className="col-name">Name</div>
                    <div className="col-email">Email</div>
                    <div className="col-phone">Phone</div>
                    {includePasscode && <div className="col-passcode">User code</div>}
                    <div className="col-status">Status</div>
                    {includeHours && <div className="col-hours">Hours</div>}
                    {includeBirthday && <div className="col-birthday">Birthday</div>}
                    {includeEmergency && <div className="col-emergency">Emergency Contact</div>}
                    {includeEmergency && <div className="col-emergency-phone">Emergency Phone</div>}
                  </div>
                  {sortVolunteers(filteredVolunteers).slice(0, 10).map((volunteer) => (
                    <div key={volunteer.id} className="table-row">
                      <div className="col-name">
                        {volunteer.firstName} {volunteer.lastName}
                        {volunteer.isMinor && <span className="minor-badge">Minor</span>}
                      </div>
                      <div className="col-email">{volunteer.email}</div>
                      <div className="col-phone">{volunteer.phoneNumber}</div>
                      {includePasscode && <div className="col-passcode">{volunteer.passcode}</div>}
                      <div className="col-status">
                        <span className={`status-badge ${volunteer.isActive ? 'active' : 'inactive'}`}>
                          {volunteer.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {includeHours && <div className="col-hours">{formatListedHours(volunteer.totalHours)}</div>}
                      {includeBirthday && (
                        <div className="col-birthday">{formatBirthday(volunteer.birthMonth)}</div>
                      )}
                      {includeEmergency && (
                        <div className="col-emergency">
                          {volunteer.emergencyFirstName} {volunteer.emergencyLastName}
                        </div>
                      )}
                      {includeEmergency && (
                        <div className="col-emergency-phone">
                          {volunteer.emergencyPhoneNumber}
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredVolunteers.length > 10 && (
                    <div className="table-more">
                      ... and {filteredVolunteers.length - 10} more volunteers
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Message */}
          {message && (
            <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </div>

        <div className="reports-directory-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsDirectoryPage;
